import { RRule } from 'rrule';

/**
 * Normalizes an RFC 5545 RRULE string by stripping any leading 'RRULE:',
 * removing DTSTART components, and trimming.
 */
export const cleanRRuleString = (ruleStr: string): string => {
    if (!ruleStr) return '';
    const lines = ruleStr.split('\n');
    const rruleLine = lines.find((l) => l.startsWith('RRULE:')) || lines.find((l) => l.includes('FREQ=')) || lines[0];
    let result = rruleLine.replace(/^RRULE:/i, '').trim();
    result = result
        .split(';')
        .filter((part) => !part.toUpperCase().startsWith('DTSTART'))
        .join(';');
    return result;
};

/**
 * Aligns appointment start and end dates with a new recurrence rule.
 * If the current start date does not fall on the recurrence days (e.g., changed
 * from Monday to Wednesday), this calculates the first occurrence on or after currentStart,
 * shifting startDate and endDate while preserving the appointment's time of day and duration.
 */
export const alignDateToRecurrence = (
    currentStart: Date,
    currentEnd: Date | null | undefined,
    ruleString: string
): { startDate: Date; endDate: Date } => {
    const fallbackEnd = currentEnd ?? new Date(currentStart.getTime() + 30 * 60000);
    const cleanRule = cleanRRuleString(ruleString);
    if (!cleanRule) {
        return { startDate: currentStart, endDate: fallbackEnd };
    }

    try {
        const parsedRule = RRule.fromString(cleanRule);
        const byweekday = parsedRule.options.byweekday;
        // In JS Date, getDay() returns 0 for Sunday, 1 for Monday, ..., 6 for Saturday.
        // In RRule, weekdays are indexed 0 for Monday, 1 for Tuesday, ..., 6 for Sunday.
        const currentDayOfWeek = (currentStart.getDay() + 6) % 7;
        const isMatchingWeekday = !byweekday || byweekday.length === 0 || byweekday.includes(currentDayOfWeek);

        if (isMatchingWeekday && parsedRule.options.freq === RRule.WEEKLY) {
            return { startDate: currentStart, endDate: fallbackEnd };
        }

        const ruleWithStart = new RRule({
            ...parsedRule.origOptions,
            dtstart: currentStart,
        });

        const nextOccurrence = ruleWithStart.after(new Date(currentStart.getTime() - 1), true);
        if (!nextOccurrence) {
            return { startDate: currentStart, endDate: fallbackEnd };
        }

        const alignedStartDate = new Date(
            nextOccurrence.getFullYear(),
            nextOccurrence.getMonth(),
            nextOccurrence.getDate(),
            currentStart.getHours(),
            currentStart.getMinutes(),
            currentStart.getSeconds(),
            currentStart.getMilliseconds()
        );

        const duration = currentEnd ? currentEnd.getTime() - currentStart.getTime() : 30 * 60000;
        const alignedEndDate = new Date(alignedStartDate.getTime() + duration);

        return { startDate: alignedStartDate, endDate: alignedEndDate };
    } catch (error) {
        console.error('Error aligning date to recurrence rule:', error);
        return { startDate: currentStart, endDate: fallbackEnd };
    }
};
